import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Cropper, { Area } from 'react-easy-crop';
import { Camera, Loader2 } from 'lucide-react';
import type { AxiosError } from 'axios';
import { authApi, PublicUser } from '@/lib/auth';
import { Modal } from '@/components/ui/Modal';

const OUT_SIZE = 512;

/**
 * Avatar uploader: file picker -> crop modal -> 512x512 PNG -> POST
 * /api/auth/me/avatar. The `['auth', 'me']` query cache is patched
 * on success so the topbar avatar refreshes immediately.
 */
export function AvatarUploader({ user }: { user: PublicUser }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement | null>(null);

  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [error, setError] = useState<string | null>(null);

  const initials = useMemo(() => {
    const n = (user.name || user.email || '?').trim();
    const parts = n.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return n.slice(0, 2).toUpperCase();
  }, [user.name, user.email]);

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedArea(areaPixels);
  }, []);

  const upload = useMutation({
    mutationFn: async () => {
      if (!sourceUrl || !croppedArea) throw new Error('No crop area available.');
      const blob = await renderCroppedPng(sourceUrl, croppedArea);
      return authApi.uploadAvatar(blob);
    },
    onSuccess: (updated) => {
      qc.setQueryData(['auth', 'me'], updated);
      close();
    },
    onError: (err) => {
      const ax = err as AxiosError<{ message?: string | string[] }>;
      const raw = ax?.response?.data?.message;
      const msg = (Array.isArray(raw) ? raw[0] : raw) ?? (err as Error).message ?? 'Upload failed.';
      setError(String(msg));
    },
  });

  function close() {
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    setSourceUrl(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedArea(null);
    setError(null);
    if (fileInput.current) fileInput.current.value = '';
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setError(t('profile.not_an_image', { defaultValue: 'That file is not an image.' }) as string);
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      setError(t('profile.image_too_big', { defaultValue: 'Image is larger than 8 MB.' }) as string);
      return;
    }
    setError(null);
    setSourceUrl(URL.createObjectURL(f));
  }

  return (
    <div className="flex items-center gap-4">
      {user.pictureUrl ? (
        <img
          src={user.pictureUrl}
          alt=""
          className="h-20 w-20 rounded-full object-cover border border-ink-200"
          referrerPolicy="no-referrer"
        />
      ) : (
        <span className="h-20 w-20 inline-flex items-center justify-center rounded-full bg-ink-900 text-white text-xl font-semibold">
          {initials}
        </span>
      )}

      <div className="space-y-1">
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className="inline-flex items-center gap-2 rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm font-medium text-ink-900 hover:bg-ink-50 transition"
        >
          <Camera className="h-3.5 w-3.5" /> {t('profile.upload_photo', { defaultValue: 'Upload photo' })}
        </button>
        <p className="text-[11.5px] text-ink-500">
          {t('profile.upload_hint', { defaultValue: 'PNG, JPEG, WebP, GIF · up to 8 MB · square crop.' })}
        </p>
        <input
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={onFile}
        />
      </div>

      <Modal
        open={sourceUrl !== null}
        onOpenChange={(next) => { if (!next) close(); }}
        title={t('profile.crop_title', { defaultValue: 'Adjust your photo' })}
        description={t('profile.crop_subtitle', { defaultValue: 'Drag to position, use the slider to zoom.' })}
      >
        <div className="space-y-3">
          {sourceUrl && (
            <div className="relative w-full h-72 bg-ink-900/95 rounded-xl overflow-hidden">
              <Cropper
                image={sourceUrl}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
          )}
          <div className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-[0.14em] text-ink-500 font-semibold">
              {t('profile.zoom', { defaultValue: 'Zoom' })}
            </span>
            <input
              type="range"
              min={1}
              max={4}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1"
            />
          </div>
          {error && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-[13px]">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={close}
              disabled={upload.isPending}
              className="rounded-lg border border-ink-300 bg-white px-3.5 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              type="button"
              onClick={() => upload.mutate()}
              disabled={upload.isPending || !croppedArea}
              className="inline-flex items-center gap-1.5 rounded-lg bg-ink-900 text-white px-3.5 py-2 text-sm font-medium hover:bg-ink-800 disabled:opacity-50"
            >
              {upload.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {upload.isPending
                ? t('profile.uploading', { defaultValue: 'Uploading…' })
                : t('profile.save_photo', { defaultValue: 'Save photo' })}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

async function renderCroppedPng(src: string, area: Area): Promise<Blob> {
  const img = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = OUT_SIZE;
  canvas.height = OUT_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable.');
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, OUT_SIZE, OUT_SIZE);
  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/png', 0.92),
  );
  if (!blob) throw new Error('Failed to encode PNG.');
  return blob;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}
